// Lite edit-mode: element picker.
//
// When the snippet detects `?steppr_edit=true`, it boots this picker instead
// of the normal tour runtime. The customer clicks an element on their own
// site; we generate a robust CSS selector and postMessage it back to the
// opening dashboard window.
//
// No API auth is required here - writes happen in the dashboard tab, not
// from the customer's domain. The picker only reports selectors upstream.
//
// Security: messages are posted to the opener with an explicit target origin
// taken from `?steppr_opener=` (set by the dashboard when opening the window).
// The dashboard filters incoming messages by `event.source` against the
// WindowProxy it kept when opening.

type PickerMessage =
  | { type: 'steppr:picker:ready' }
  | {
      type: 'steppr:picker:selected';
      selector: string;
      preview: { tag: string; text: string };
    }
  | { type: 'steppr:picker:closed' };

// ---------- Selector generation ----------

function cssEscape(v: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(v);
  }
  return v.replace(/["\\]/g, '\\$&');
}

// Reject identifiers that look auto-generated. Heuristics are intentionally
// loose - we'd rather keep a questionable id and let uniqueness prove it
// works than drop a working identifier.
function isStableIdent(v: string): boolean {
  if (v.length < 2 || v.length > 64) return false;
  if (v.startsWith(':')) return false; // React useId() etc.
  if (/^(css|sc|mui)-[a-z0-9]+$/i.test(v)) return false;
  if (/^[a-f0-9]{8,}$/i.test(v)) return false; // pure hex hash
  return true;
}

// Skip classes that are obvious CSS-in-JS hashes. Tailwind / utility classes
// stay - they're ugly selectors but they're stable across rebuilds.
function isStableClass(cls: string): boolean {
  if (cls.length < 2) return false;
  if (/^(css|sc|mui)-/.test(cls)) return false;
  // Long lowercase-alphanumeric strings with digits smell like a hash.
  if (cls.length > 10 && /\d{4,}/.test(cls) && !/-/.test(cls)) return false;
  return true;
}

function isUnique(selector: string, scope: ParentNode = document): boolean {
  try {
    return scope.querySelectorAll(selector).length === 1;
  } catch {
    return false;
  }
}

function describe(el: Element): string {
  const tag = el.tagName.toLowerCase();

  const testid = el.getAttribute('data-testid');
  if (testid) return `${tag}[data-testid="${cssEscape(testid)}"]`;

  if (el.id && isStableIdent(el.id)) return `${tag}#${cssEscape(el.id)}`;

  const classes = Array.from(el.classList).filter(isStableClass);
  if (classes.length > 0) {
    return `${tag}.${classes.slice(0, 3).map(cssEscape).join('.')}`;
  }

  // No usable signal on this element itself - disambiguate by sibling index.
  const parent = el.parentElement;
  if (!parent) return tag;
  const siblings = Array.from(parent.children).filter((c) => c.tagName === el.tagName);
  if (siblings.length === 1) return tag;
  return `${tag}:nth-of-type(${siblings.indexOf(el) + 1})`;
}

// Priority: data-testid → stable id → ancestor-chain path.
function buildSelector(el: Element): string {
  const testid = el.getAttribute('data-testid');
  if (testid) {
    const sel = `[data-testid="${cssEscape(testid)}"]`;
    if (isUnique(sel)) return sel;
  }

  if (el.id && isStableIdent(el.id)) {
    const sel = `#${cssEscape(el.id)}`;
    if (isUnique(sel)) return sel;
  }

  // Walk up, prepending each ancestor until the combined selector is unique
  // or we hit the depth ceiling. Five levels is enough to disambiguate in
  // practice without producing unreadable selectors.
  const parts: string[] = [describe(el)];
  if (isUnique(parts.join(' > '))) return parts.join(' > ');

  let cur: Element | null = el.parentElement;
  let depth = 0;
  while (cur && depth < 5) {
    parts.unshift(describe(cur));
    const combined = parts.join(' > ');
    if (isUnique(combined)) return combined;
    cur = cur.parentElement;
    depth++;
  }

  return parts.join(' > ');
}

// ---------- Picker UI ----------

const OVERLAY_CSS = `
:host {
  all: initial;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
* {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.toolbar {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2147483647;
  background: #0f172a;
  color: #f8fafc;
  padding: 10px 14px;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  font-size: 13px;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: calc(100vw - 32px);
}
.toolbar .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #38bdf8;
  animation: pulse 1.2s ease-out infinite;
}
.toolbar.paused {
  background: #1e293b;
}
.toolbar.paused .dot {
  background: #fbbf24;
  animation: none;
}
.toolbar button {
  font: inherit;
  background: transparent;
  color: inherit;
  border: 1px solid rgba(248, 250, 252, 0.25);
  padding: 5px 10px;
  border-radius: 6px;
  cursor: pointer;
}
.toolbar button:hover { background: rgba(248, 250, 252, 0.1); }
.toolbar .primary {
  background: #38bdf8;
  color: #0f172a;
  border-color: #38bdf8;
  font-weight: 600;
}
.toolbar .primary:hover { background: #7dd3fc; }

.highlight {
  position: fixed;
  pointer-events: none;
  z-index: 2147483646;
  border: 2px solid #38bdf8;
  background: rgba(56, 189, 248, 0.12);
  border-radius: 2px;
  transition: top 60ms linear, left 60ms linear, width 60ms linear, height 60ms linear;
}
.highlight.confirmed {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.12);
}
.preview {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  opacity: 0.85;
  max-width: 360px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
`;

type PickerState = {
  mounted: boolean;
  opener: Window | null;
  openerOrigin: string;
  host: HTMLElement | null;
  shadow: ShadowRoot | null;
  highlight: HTMLDivElement | null;
  toolbar: HTMLDivElement | null;
  preview: HTMLSpanElement | null;
  hovered: Element | null;
  selected: Element | null;
  selectedSelector: string | null;
  // When paused, all click capture and hover highlighting is disabled so the
  // customer can interact with their own app - clicking through to a
  // different page, opening a menu, scrolling to the right element. The
  // toolbar stays visible with a Resume button. After navigation, the new
  // page's picker boots from sessionStorage and starts active again.
  paused: boolean;
};

const picker: PickerState = {
  mounted: false,
  opener: null,
  openerOrigin: '*',
  host: null,
  shadow: null,
  highlight: null,
  toolbar: null,
  preview: null,
  hovered: null,
  selected: null,
  selectedSelector: null,
  paused: false,
};

function post(msg: PickerMessage) {
  if (!picker.opener) return;
  try {
    picker.opener.postMessage(msg, picker.openerOrigin);
  } catch {
    // Opener navigated away or cross-origin block - nothing we can do.
  }
}

// Return true if the event target is inside our picker UI. We never want to
// pick / highlight our own toolbar.
function isOwnUi(el: Element | null): boolean {
  if (!el || !picker.host) return false;
  return picker.host.contains(el) || el === picker.host;
}

function positionHighlight(el: Element) {
  if (!picker.highlight) return;
  const r = el.getBoundingClientRect();
  picker.highlight.style.top = r.top + 'px';
  picker.highlight.style.left = r.left + 'px';
  picker.highlight.style.width = r.width + 'px';
  picker.highlight.style.height = r.height + 'px';
  picker.highlight.style.display = 'block';
}

function hideHighlight() {
  if (picker.highlight) picker.highlight.style.display = 'none';
}

function previewText(el: Element): string {
  const raw = (el.textContent ?? '').replace(/\s+/g, ' ').trim();
  return raw.length > 48 ? raw.slice(0, 45) + '\u2026' : raw;
}

function onMouseOver(e: MouseEvent) {
  const target = e.target as Element | null;
  if (!target || isOwnUi(target)) return;
  if (picker.paused) return;
  if (picker.selected) return; // waiting for confirm; don't chase the cursor
  picker.hovered = target;
  positionHighlight(target);
  if (picker.preview) picker.preview.textContent = `<${target.tagName.toLowerCase()}> ${previewText(target)}`;
}

function onClickCapture(e: MouseEvent) {
  const target = e.target as Element | null;
  if (!target || isOwnUi(target)) return;
  // While paused, let the customer's own click handlers run - that's how they
  // navigate to the page they actually want to pick on. The picker re-arms
  // automatically on the next page via the sessionStorage marker.
  if (picker.paused) return;
  e.preventDefault();
  e.stopPropagation();
  picker.selected = target;
  picker.selectedSelector = buildSelector(target);
  positionHighlight(target);
  picker.highlight?.classList.add('confirmed');
  renderToolbar();
}

function setPaused(next: boolean) {
  picker.paused = next;
  if (next) {
    // Drop any in-flight selection / highlight so we don't leave a confirmed
    // box framing an element the user is about to navigate away from.
    picker.hovered = null;
    picker.selected = null;
    picker.selectedSelector = null;
    picker.highlight?.classList.remove('confirmed');
    hideHighlight();
  }
  renderToolbar();
}

function onReposition() {
  const active = picker.selected ?? picker.hovered;
  if (active) positionHighlight(active);
}

function confirmSelection() {
  if (!picker.selected || !picker.selectedSelector) return;
  const el = picker.selected;
  post({
    type: 'steppr:picker:selected',
    selector: picker.selectedSelector,
    preview: { tag: el.tagName.toLowerCase(), text: previewText(el) },
  });
  clearEditSession();
  // Let the dashboard-side close us; close ourselves if it doesn't within a
  // tick. Some browsers block window.close() from the opened window unless
  // it was window.open'd with no name, so this is best-effort.
  setTimeout(() => {
    post({ type: 'steppr:picker:closed' });
    try {
      window.close();
    } catch {
      // Left open - user can close the tab manually.
    }
  }, 150);
}

// Drop the sessionStorage marker so a future navigation to the customer's
// app within this same tab doesn't accidentally re-boot edit mode. The
// marker is window-scoped so it'd die with the tab anyway, but a popup
// that fails to window.close() and gets reused for normal browsing
// shouldn't carry stale edit state.
function clearEditSession() {
  try {
    sessionStorage.removeItem('__steppr_edit_session');
  } catch {
    /* ignore */
  }
}

function clearSelection() {
  picker.selected = null;
  picker.selectedSelector = null;
  picker.highlight?.classList.remove('confirmed');
  hideHighlight();
  renderToolbar();
}

function cancel() {
  post({ type: 'steppr:picker:closed' });
  clearEditSession();
  try {
    window.close();
  } catch {
    // ignore
  }
}

function renderToolbar() {
  if (!picker.toolbar) return;
  picker.toolbar.replaceChildren();
  picker.toolbar.classList.toggle('paused', picker.paused);

  const dot = document.createElement('span');
  dot.className = 'dot';
  picker.toolbar.appendChild(dot);

  const label = document.createElement('span');
  if (picker.paused) {
    label.textContent = 'Click around freely. Resume to pick an element.';
  } else if (picker.selectedSelector) {
    label.textContent = 'Use this element?';
  } else {
    label.textContent = 'Hover an element, then click to pick it.';
  }
  picker.toolbar.appendChild(label);

  if (!picker.paused && picker.selectedSelector) {
    const preview = document.createElement('span');
    preview.className = 'preview';
    preview.textContent = picker.selectedSelector;
    picker.toolbar.appendChild(preview);

    const use = document.createElement('button');
    use.className = 'primary';
    use.textContent = 'Use this';
    use.addEventListener('click', confirmSelection);
    picker.toolbar.appendChild(use);

    const retry = document.createElement('button');
    retry.textContent = 'Pick another';
    retry.addEventListener('click', clearSelection);
    picker.toolbar.appendChild(retry);
  }

  // Pause/Resume sits between picking actions and Cancel so it's discoverable
  // but not the first thing thumb-eyes hit. Hidden during the "Use this?"
  // confirm step - at that point the only sensible next action is to commit
  // or pick again, not navigate.
  if (!picker.selectedSelector) {
    const toggle = document.createElement('button');
    toggle.textContent = picker.paused ? 'Resume picking' : 'Pause to navigate';
    toggle.title = picker.paused
      ? 'Re-enable element picking on this page.'
      : 'Click your own buttons / links to navigate. The picker comes back automatically on the next page.';
    toggle.addEventListener('click', () => setPaused(!picker.paused));
    picker.toolbar.appendChild(toggle);
  }

  const close = document.createElement('button');
  close.textContent = 'Cancel';
  close.addEventListener('click', cancel);
  picker.toolbar.appendChild(close);

  picker.preview = picker.toolbar.querySelector('.preview');
}

export function bootPicker(): void {
  if (picker.mounted) return;
  picker.mounted = true;

  // Read the opener origin that the dashboard embeds in the URL so we can
  // target postMessages safely. After the customer navigates within their
  // app and the URL params get stripped, we fall back to the value stashed
  // in sessionStorage by the first boot. '*' in dev where neither is set -
  // the dashboard still filters by `event.source`.
  try {
    const params = new URLSearchParams(window.location.search);
    let opener = params.get('steppr_opener');
    if (!opener) {
      try {
        const stash = sessionStorage.getItem('__steppr_edit_session');
        if (stash) {
          const parsed = JSON.parse(stash) as { opener?: string };
          if (parsed.opener) opener = parsed.opener;
        }
      } catch {
        /* ignore */
      }
    }
    picker.openerOrigin = opener && opener.length > 0 ? opener : '*';
  } catch {
    picker.openerOrigin = '*';
  }
  picker.opener = window.opener;

  // Shadow-rooted host so our CSS doesn't bleed into the customer's site.
  const host = document.createElement('div');
  host.id = '__steppr_picker__';
  host.style.all = 'initial';
  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = OVERLAY_CSS;
  shadow.appendChild(style);

  const highlight = document.createElement('div');
  highlight.className = 'highlight';
  highlight.style.display = 'none';
  shadow.appendChild(highlight);

  const toolbar = document.createElement('div');
  toolbar.className = 'toolbar';
  shadow.appendChild(toolbar);

  document.body.appendChild(host);
  picker.host = host;
  picker.shadow = shadow;
  picker.highlight = highlight;
  picker.toolbar = toolbar;

  // Stop pointer events that originate inside the toolbar from bubbling out
  // to the customer's app - otherwise click-outside handlers (e.g. on a
  // modal the user opened while paused) treat our toolbar clicks as
  // "outside" and dismiss the modal. Bubble-phase listeners on the host
  // run AFTER the toolbar buttons' own click handlers, so this doesn't
  // break Pause / Resume / Cancel firing. Multiple event types because
  // popular click-outside libraries listen for various points along the
  // mousedown → mouseup → click sequence.
  for (const evt of ['click', 'mousedown', 'mouseup', 'pointerdown', 'pointerup', 'touchstart']) {
    host.addEventListener(evt, (e) => e.stopPropagation());
  }

  renderToolbar();

  document.addEventListener('mouseover', onMouseOver, true);
  document.addEventListener('click', onClickCapture, true);
  document.addEventListener('scroll', onReposition, { capture: true, passive: true });
  window.addEventListener('resize', onReposition);

  // Signal the dashboard that we're ready to pick.
  post({ type: 'steppr:picker:ready' });
}
